import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, CalendarX2, Trash2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

export const AdminBlackoutsCard = ({ onChange }) => {
  const [blackouts, setBlackouts] = useState([]);
  const [dates, setDates] = useState([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/availability/blackouts`, authHeader());
      setBlackouts(res.data);
    } catch {
      setBlackouts([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const block = async () => {
    if (!dates.length) {
      toast.error("Pick at least one date to block");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/availability/blackouts`,
        { dates: dates.map((d) => format(d, "yyyy-MM-dd")), reason },
        authHeader()
      );
      const { slots_hidden: hidden, booked_conflicts: booked } = res.data;
      toast.success(`Blocked — ${hidden} open slot(s) hidden from clients`);
      if (booked) {
        toast.warning(`${booked} slot(s) on those dates are already booked — reschedule or cancel them yourself`);
      }
      setDates([]);
      setReason("");
      load();
      onChange?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not block those dates");
    } finally {
      setSaving(false);
    }
  };

  const unblock = async (id) => {
    try {
      const res = await axios.delete(`${API}/admin/availability/blackouts/${id}`, authHeader());
      toast.success(res.data.message);
      load();
      onChange?.();
    } catch {
      toast.error("Could not unblock that date");
    }
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="admin-blackouts-card">
      <h2 className="font-serif text-xl text-white mb-2 flex items-center gap-2">
        <CalendarX2 className="w-5 h-5 text-[#D4AF37]" />
        Blocked dates
      </h2>
      <p className="text-slate-400 text-sm mb-6 max-w-2xl">
        Holidays and one-offs. Slots on a blocked date are hidden from clients and never re-created by the weekly
        schedule — nothing is deleted, so unblocking brings them straight back.
      </p>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <Calendar
            mode="multiple"
            selected={dates}
            onSelect={setDates}
            disabled={(date) => date < new Date(new Date().toDateString())}
            className="bg-slate-900 border border-slate-800"
            data-testid="blackout-calendar"
          />
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional) — e.g. Easter break"
            data-testid="blackout-reason-input"
            className="w-full bg-slate-950 border border-slate-800 text-white h-12 px-3 mt-4 text-sm placeholder:text-slate-600"
          />
          <button
            onClick={block}
            disabled={saving || !dates.length}
            data-testid="block-dates-btn"
            className="mt-4 w-full bg-[#D4AF37] text-slate-950 h-12 text-xs uppercase tracking-widest hover:bg-[#B5952F] disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Block {dates.length || ""} date{dates.length === 1 ? "" : "s"}
          </button>
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-4">
            Currently blocked ({blackouts.length})
          </p>
          {blackouts.length === 0 ? (
            <div className="border border-dashed border-slate-700 p-8 text-center" data-testid="blackouts-empty">
              <p className="text-slate-500 text-sm">Nothing blocked — the weekly schedule runs uninterrupted.</p>
            </div>
          ) : (
            <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-2" data-testid="blackouts-list">
              {blackouts.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-3 border border-slate-800 px-4 py-3"
                  data-testid={`blackout-row-${b.date}`}
                >
                  <div>
                    <p className="text-white text-sm">
                      {new Date(`${b.date}T00:00:00`).toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                    {b.reason && <p className="text-slate-500 text-xs mt-0.5">{b.reason}</p>}
                  </div>
                  <button
                    onClick={() => unblock(b.id)}
                    data-testid={`unblock-btn-${b.date}`}
                    className="text-slate-500 hover:text-red-400 transition-colors"
                    title="Unblock"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
